<template>
  <div class="person-select__department-list">
    <div class="person-select__search">
      <el-input
        v-model.trim="keyword"
        clearable
        placeholder="输入部门名称"
        @input="treeRef?.filter($event)"
      >
        <template #prefix>
          <i class="icon-search-line person-select__search-icon t-iconfont" />
        </template>
      </el-input>
    </div>

    <div v-loading="loading" class="person-select__tree">
      <el-tree
        ref="treeRef"
        check-on-click-node
        check-strictly
        :data="departmentList"
        :default-expanded-keys="defaultExpandedKeys"
        empty-text="暂无部门"
        :expand-on-click-node="false"
        :filter-node-method="filterDepartmentNode"
        :icon="treeExpandIcon"
        node-key="pid"
        :props="departmentTreeProps"
        show-checkbox
        @check-change="handleCheckChange"
      >
        <template #default="{ data }">
          <span class="person-select__dept-name">{{ data.text }}</span>
        </template>
      </el-tree>
    </div>
  </div>
</template>

<script setup>
import { h, nextTick, ref, watch } from 'vue'
import { getDepartmentlist } from '@/api/ai-board'

defineOptions({
  name: 'DepartmentList'
})

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  selectedList: {
    type: Array,
    default: () => []
  }
})

const emits = defineEmits(['check-change'])

const keyword = ref('')
const loading = ref(false)
const departmentList = ref([])
const treeRef = ref(null)
const defaultExpandedKeys = ref([])
const treeExpandIcon = {
  render: () => h('i', { class: 'icon-arrow-down-s-line t-iconfont' })
}
const departmentTreeProps = {
  children: 'nodes',
  label: 'text'
}
// 部门树和权限配置的 ID 类型可能不同，统一转字符串后再判断是否已选中
const isSameId = (left, right) => String(left) === String(right)

const isChecked = (item) =>
  props.selectedList.some(
    (selected) => selected.property === 'dept' && isSameId(selected.id, item.pid)
  )

const syncCheckedKeys = () => {
  const keys = props.selectedList.filter((item) => item.property === 'dept').map((item) => item.id)
  treeRef.value?.setCheckedKeys(keys)
}

const getDepartmentList = async () => {
  loading.value = true
  try {
    const res = await getDepartmentlist()
    departmentList.value = res?.result?.departmentTree || []
    defaultExpandedKeys.value = departmentList.value[0]?.pid ? [departmentList.value[0].pid] : []
    nextTick(syncCheckedKeys)
  } catch (error) {
    console.error(error)
    departmentList.value = []
    defaultExpandedKeys.value = []
  } finally {
    loading.value = false
  }
}

const filterDepartmentNode = (value, data) => {
  if (!value) return true
  return data.text.includes(value)
}

const handleCheckChange = (item, checked) => {
  if (checked === isChecked(item)) return
  emits('check-change', item, checked)
  nextTick(syncCheckedKeys)
}

watch(
  () => props.show,
  (visible) => {
    if (!visible) return
    keyword.value = ''
    getDepartmentList()
  },
  { immediate: true }
)

watch(
  () => props.selectedList,
  () => nextTick(syncCheckedKeys),
  { deep: true }
)
</script>

<style scoped lang="less">
.person-select {
  &__department-list {
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 16px 16px 0;
  }

  &__search {
    margin-top: 1px;

    :deep(.el-input__wrapper) {
      border-radius: 8px;
      box-shadow: 0 0 0 1px #e5e6eb inset;
    }
  }

  &__search-icon {
    color: #969799;
  }

  &__tree {
    flex: 1;
    box-sizing: border-box;
    margin-top: 8px;
    overflow-y: auto;

    :deep(.el-tree) {
      color: #323233;
      font-size: 14px;
    }

    :deep(.el-tree-node__content) {
      height: 36px;
      border-radius: 8px;
    }

    :deep(.el-tree-node__content:hover) {
      background: #f7f8fa;
    }

    :deep(.el-tree-node__expand-icon) {
      color: #969799;
      font-size: 16px;
      transform: rotate(-90deg);
    }

    :deep(.el-tree-node__expand-icon.expanded) {
      transform: rotate(0deg);
    }

    :deep(.el-checkbox__inner) {
      width: 14px;
      height: 14px;
    }

    :deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
      background-color: #7a52f4;
      border-color: #7a52f4;
    }
  }

  &__dept-name {
    overflow: hidden;
    color: #323233;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
